// Native-print bridge for the counter POS.
//
// When running inside the pos-app/ Capacitor shell, the iPad talks to
// the LAN printer directly via a custom TcpPrint plugin (see
// pos-app/packages/tcp-print/). When running in Safari or the customer
// app, this module short-circuits so the browser POS keeps working —
// the print will just be a no-op with a clear "printer only in native
// app" message.
//
// Important: the POS JS bundle is served from Azure (server.url → /pos),
// so it never imports the local `st-barb-tcp-print` npm package. The
// JS↔native bridge is created at call time with Capacitor.registerPlugin
// ('TcpPrint'), which matches the CAP_PLUGIN(TcpPrintPlugin, "TcpPrint", …)
// registration on the Swift/Obj-C side.

let cachedTcpPrint = null;
let pluginResolved = false;
let cachedCapacitor = null;

// Read Capacitor via `window.Capacitor` if it exists. The pos-app
// injects a global Capacitor object when the WebView starts; if we're
// running in a plain browser (customer app / dev / Safari POS), it's
// undefined and every native path short-circuits to false.
function getCapacitor() {
  if (cachedCapacitor !== null) return cachedCapacitor;
  cachedCapacitor = (typeof window !== 'undefined' && window.Capacitor) || null;
  return cachedCapacitor;
}

// True inside a Capacitor native shell (pos-app or customer app). Callers
// use this to decide whether to ask the server for client_prints payloads.
// Actual send() still requires the TcpPrint native plugin (pos-app only).
export function isNativePrintAvailable() {
  const cap = getCapacitor();
  if (!cap) return false;
  if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform();
  return false;
}

// Create (or reuse) the TcpPrint plugin proxy.
//
// Capacitor does NOT auto-expose custom plugins under Plugins.TcpPrint
// when the web content is loaded from a remote URL — the Azure bundle
// never imported st-barb-tcp-print. registerPlugin('TcpPrint') builds
// the JS proxy that calls the native CAP_PLUGIN of the same name.
function getTcpPrintPlugin() {
  if (pluginResolved) return cachedTcpPrint;
  pluginResolved = true;

  const cap = getCapacitor();
  if (!cap || typeof cap.isNativePlatform !== 'function' || !cap.isNativePlatform()) {
    cachedTcpPrint = null;
    return null;
  }

  if (cap.Plugins?.TcpPrint && typeof cap.Plugins.TcpPrint.send === 'function') {
    cachedTcpPrint = cap.Plugins.TcpPrint;
    return cachedTcpPrint;
  }

  if (typeof cap.registerPlugin === 'function') {
    try {
      cachedTcpPrint = cap.registerPlugin('TcpPrint');
      return cachedTcpPrint;
    } catch (err) {
      console.error('[nativePrint] registerPlugin(TcpPrint) failed:', err);
      cachedTcpPrint = null;
      return null;
    }
  }

  console.error('[nativePrint] Capacitor.registerPlugin is not available');
  cachedTcpPrint = null;
  return null;
}

// Send a single docket to the LAN printer. Docket is the shape the
// server returns: { host, port, data (base64), orderNumber, label, ... }.
// Rejects with a { docket, error } object so the caller can present a
// per-docket "Reprint just St Barbara" prompt without losing which
// one failed.
export async function printDocketNative(docket) {
  const plugin = getTcpPrintPlugin();
  if (!plugin) {
    throw Object.assign(
      new Error(
        'Native print plugin is not available. Rebuild the St Barb POS app ' +
          'after `npx cap sync ios` so the TcpPrint plugin is linked, or ' +
          'confirm you are running the native POS app (not Safari).'
      ),
      { docket, code: 'NO_PLUGIN' }
    );
  }
  if (!docket || !docket.host || !docket.port || !docket.data) {
    throw Object.assign(
      new Error('Invalid docket payload'),
      { docket, code: 'BAD_DOCKET' }
    );
  }
  try {
    const result = await plugin.send({
      host: docket.host,
      port: Number(docket.port),
      data: docket.data,
    });
    return { ok: true, docket, result };
  } catch (err) {
    // Capacitor wraps native errors in an object with .message; some
    // paths (e.g. TcpPrintError.timedOut in Swift) also give a code.
    // "UNIMPLEMENTED" means the Swift plugin isn't in this iOS build.
    const message = err?.message || String(err) || 'Print failed';
    const code = err?.code || 'PRINT_FAILED';
    if (code === 'UNIMPLEMENTED' || /not implemented/i.test(message)) {
      throw Object.assign(
        new Error(
          'TcpPrint native plugin is missing from this iOS build. ' +
            'On the Mac: cd pos-app && npx cap sync ios, then rebuild in Xcode (⌘R).'
        ),
        { docket, code: 'UNIMPLEMENTED' }
      );
    }
    throw Object.assign(new Error(message), { docket, code });
  }
}

// Fire dockets in series (one at a time) so a wedged printer on the
// first docket doesn't cascade into three parallel timeouts. Returns
// { printed, failed } — never throws, since a failed print must never
// break the caller's post-sale UI (the completion splash still needs
// to show). Failed dockets can be reprinted individually from the
// summary; caller decides how to surface `failed` to staff.
export async function printDocketsNative(dockets) {
  if (!Array.isArray(dockets) || dockets.length === 0) {
    return { printed: [], failed: [] };
  }
  const printed = [];
  const failed = [];
  for (const docket of dockets) {
    try {
      const res = await printDocketNative(docket);
      printed.push(res);
    } catch (err) {
      failed.push({ docket, error: err.message, code: err.code });
      // Keep going — one printer error must not halt the rest of the
      // batch (e.g. two-kitchen sale where St Barbara's printer is
      // down but St Noufer's is fine, we still want St Noufer to
      // print).
    }
  }
  return { printed, failed };
}
